import React, { useState } from 'react';
import { Form, Input, Button, Row, Col, Space, Divider, Typography, Avatar, Card } from 'antd';
import {
    TeamOutlined,
    BankOutlined,
    MobileOutlined,
    MailOutlined,
    UserOutlined,
    PlusOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { channelPartnerService } from '../../services/channelPartnerService';
import { notification } from 'antd';

const { TextArea } = Input;
const { Title, Text } = Typography;

const ChannelPartnerForm = ({ partner, onSuccess, onCancel }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        if (partner) {
            form.setFieldsValue(partner);
        } else {
            form.resetFields();
        }

    }, [partner, form]);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);

            const data = { ...values };
            Object.keys(data).forEach(key => {
                if (data[key] === '' || data[key] === undefined) {
                    data[key] = null;
                }
            });

            if (partner) {
                await channelPartnerService.updateChannelPartner(partner.id, data);
                notification.success({
                    message: 'Success',
                    description: 'Channel Partner updated successfully'
                });
            } else {
                await channelPartnerService.createChannelPartner(data);
                notification.success({
                    message: 'Success',
                    description: 'Channel Partner created successfully'
                });
            }

            onSuccess();
        } catch (error) {
            notification.error({
                message: 'Error',
                description: error.response?.data?.error || error.message || 'Failed to save channel partner'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ padding: '0 10px' }}
        >
            <div style={{ marginBottom: 24 }}>
                <Space size="middle">
                    <Avatar size="large" icon={<TeamOutlined />} style={{ backgroundColor: '#1890ff' }} />
                    <div>
                        <Title level={4} style={{ margin: 0 }}>
                            {partner ? 'Edit Channel Partner Details' : 'Add New Channel Partner'}
                        </Title>
                        <Text type="secondary">Manage channel partner information and bank details.</Text>
                    </div>
                </Space>
            </div>

            <Divider orientation="left"><Space><TeamOutlined />Basic Information</Space></Divider>
            <Row gutter={24}>
                <Col span={12}>
                    <Form.Item
                        label="Partner Name"
                        name="partnerName"
                        validateTrigger="onChange"
                        rules={[
                            { required: true, message: 'Please enter partner name' },
                            { pattern: /^[^0-9]*$/, message: 'Numbers are not allowed' }
                        ]}
                    >
                        <Input prefix={<TeamOutlined />} placeholder="Channel Partner Name" size="large" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="GSTN"
                        name="gstn"
                        validateTrigger="onChange"
                        rules={[
                            { pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, message: 'Invalid GST Format' }
                        ]}
                        normalize={(value) => value?.toUpperCase()}
                    >
                        <Input placeholder="GST Identification Number" size="large" maxLength={15} style={{ textTransform: 'uppercase' }} />
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Form.Item
                        label="Partner Address"
                        name="partnerAddress"
                    >
                        <TextArea rows={3} placeholder="Complete address" size="large" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Partner Mobile"
                        name="partnerMobile"
                        validateTrigger="onChange"
                        rules={[
                            { required: true, message: 'Please enter mobile number' },
                            { pattern: /^\d{10}$/, message: 'Please enter a valid 10-digit number' }
                        ]}
                    >
                        <Input
                            prefix={<MobileOutlined />}
                            placeholder="10-digit mobile number"
                            size="large"
                            maxLength={10}
                            onKeyPress={(e) => {
                                if (!/[0-9]/.test(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Partner Email"
                        name="partnerEmail"
                        rules={[{ type: 'email', message: 'Please enter valid email' }]}
                    >
                        <Input prefix={<MailOutlined />} placeholder="partner@example.com" size="large" />
                    </Form.Item>
                </Col>
            </Row>

            <Divider orientation="left"><Space><UserOutlined />Contact Person</Space></Divider>
            <Row gutter={24}>
                <Col span={8}>
                    <Form.Item
                        label="Contact Name"
                        name="contactName"
                        rules={[
                            { pattern: /^[^0-9]*$/, message: 'Numbers are not allowed' }
                        ]}
                    >
                        <Input prefix={<UserOutlined />} placeholder="Contact person name" size="large" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        label="Contact Number"
                        name="contactNumber"
                        rules={[{ pattern: /^\d{10}$/, message: 'Please enter a valid 10-digit mobile number' }]}
                    >
                        <Input
                            prefix={<MobileOutlined />}
                            placeholder="Contact mobile"
                            size="large"
                            maxLength={10}
                            onKeyPress={(e) => {
                                if (!/[0-9]/.test(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        label="Contact Email"
                        name="contactEmail"
                        rules={[{ type: 'email', message: 'Please enter valid email' }]}
                    >
                        <Input prefix={<MailOutlined />} placeholder="contact@example.com" size="large" />
                    </Form.Item>
                </Col>
            </Row>

            <Divider orientation="left"><Space><BankOutlined />Bank Details</Space></Divider>
            <Row gutter={24}>
                <Col span={12}>
                    <Form.Item
                        label="Account Name"
                        name="accountName"
                        rules={[
                            { pattern: /^[^0-9]*$/, message: 'Numbers are not allowed' }
                        ]}
                    >
                        <Input placeholder="Account holder name" size="large" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Bank Name"
                        name="bankName"
                    >
                        <Input placeholder="Bank name" size="large" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Branch Name"
                        name="branchName"
                    >
                        <Input placeholder="Branch name" size="large" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Account Number"
                        name="accountNumber"
                        rules={[
                            { pattern: /^\d+$/, message: 'Digits only' }
                        ]}
                    >
                        <Input
                            placeholder="Account number"
                            size="large"
                            onKeyPress={(e) => {
                                if (!/[0-9]/.test(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="IFSC Code"
                        name="ifscCode"
                        rules={[
                            { pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/, message: 'Invalid IFSC Format' }
                        ]}
                        normalize={(value) => value?.toUpperCase()}
                    >
                        <Input placeholder="IFSC code" size="large" maxLength={11} style={{ textTransform: 'uppercase' }} />
                    </Form.Item>
                </Col>
            </Row>


            <div style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: '100%',
                borderTop: '1px solid #e9e9e9',
                padding: '10px 16px',
                background: '#fff',
                textAlign: 'right',
                zIndex: 1,
            }}>
                <Space>
                    <Button onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading} size="large">
                        {partner ? 'Update' : 'Create'} Partner
                    </Button>
                </Space>
            </div>
        </Form>
    );
};

export default ChannelPartnerForm;
